/**
 * Admin Configuration Page
 *
 * Main page for tenant administrators to manage TARA calculation configurations
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useConfiguration } from '../../contexts/ConfigurationContext';
import { PageLayout } from '../../components/layout';
import UserInfo from '../../components/common/UserInfo';
import InstructionPanel from '../../components/ui/InstructionPanel/InstructionPanel';
import FormModal from '../../components/ui/FormModal/FormModal';
import { Icon } from '../../components/ui/Icon';
import { Button } from '../../components/ui/Button2';
import { Badge } from '../../components/ui/Badge';
import { useConfirm } from '../../components/ui/AlertDialog';
import ConfigurationSkeleton from './components/ConfigurationSkeleton';
import RiskMatrixEditor from './components/RiskMatrixEditor';
import CALMatrixEditor from './components/CALMatrixEditor';
import ThresholdEditor from './components/ThresholdEditor';
import ScoringFactorsEditor from './components/ScoringFactorsEditor';
import FormulaEditor from './components/FormulaEditor';
import ImpactRatingEditor from './components/ImpactRatingEditor';
import CALCriteriaEditor from './components/CALCriteriaEditor';
import VersionHistory from './components/VersionHistory';
import AuditLog from './components/AuditLog';
import styles from './AdminConfiguration.module.css';

// FormModal field schema for the Save Configuration dialog.
// Declared at module scope so each render reuses the same reference.
const SAVE_DIALOG_FIELDS = [
  {
    name: 'description',
    type: 'text',
    label: 'Description',
    placeholder: 'e.g., Q4 2024 Updated Risk Scoring',
    required: true,
    autoFocus: true,
    maxLength: 200,
    validate: (value) =>
      (value || '').trim().length === 0
        ? 'Description is required'
        : null,
  },
  {
    name: 'notes',
    type: 'textarea',
    label: 'Notes (optional)',
    placeholder: 'Additional notes about this configuration...',
    rows: 4,
    maxLength: 500,
    helpText:
      'This will create a new configuration version. The current version stays active until you activate the new one.',
  },
];

const SECTIONS = {
  OVERVIEW: 'overview',
  RISK_MATRIX: 'risk_matrix',
  CAL_MATRIX: 'cal_matrix',
  CAL_CRITERIA: 'cal_criteria',
  THRESHOLDS: 'thresholds',
  SCORING_FACTORS: 'scoring_factors',
  FORMULAS: 'formulas',
  IMPACT_RATINGS: 'impact_ratings',
  VERSIONS: 'versions',
  AUDIT_LOG: 'audit_log'
};

const VALID_SECTION_IDS = new Set(Object.values(SECTIONS));
const DEFAULT_SECTION = SECTIONS.OVERVIEW;

export default function AdminConfigurationPage({ embedded = false }) {
  // Active section is sourced from the `?section=` query param so the
  // selection survives page refreshes and is shareable via URL.
  // Invalid / missing values fall back to DEFAULT_SECTION. Writes go
  // through setActiveSection() below, which preserves any other query
  // params (e.g., the parent workspace's `?tab=admin-config`).
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const activeSection =
    sectionParam && VALID_SECTION_IDS.has(sectionParam)
      ? sectionParam
      : DEFAULT_SECTION;

  const setActiveSection = (next) => {
    setSearchParams(
      (prev) => {
        const updated = new URLSearchParams(prev);
        if (next === DEFAULT_SECTION) {
          updated.delete('section');
        } else {
          updated.set('section', next);
        }
        return updated;
      },
      { replace: true },
    );
  };

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveMetadata, setSaveMetadata] = useState({
    description: '',
    notes: ''
  });

  const {
    config,
    loading,
    error,
    isModified,
    fetchActiveConfiguration,
    fetchVersions,
    fetchAuditLog,
    saveConfiguration,
    resetToOriginal,
  } = useConfiguration();

  useEffect(() => {
    // Load initial data
    fetchActiveConfiguration();
    fetchVersions();
    fetchAuditLog();
  }, [fetchActiveConfiguration, fetchVersions, fetchAuditLog]);

  // Called by FormModal.onSubmit — receives the validated form values.
  // Throws on failure so FormModal surfaces the message inline.
  const handleSave = async (values) => {
    const result = await saveConfiguration({
      description: values.description,
      notes: values.notes,
      created_by: 'current_user', // Will be set by backend from JWT
    });
    if (result.success) {
      setShowSaveDialog(false);
      setSaveMetadata({ description: '', notes: '' });
    } else {
      throw new Error(result.error || 'Save failed');
    }
  };

  const { confirm, confirmDialog } = useConfirm();

  const handleCancel = async () => {
    if (!isModified) return;
    if (await confirm({
      title: 'Discard changes?',
      description: 'You have unsaved changes. Discard them?',
      confirmLabel: 'Discard',
      destructive: true,
    })) {
      resetToOriginal();
    }
  };

  const renderSectionContent = () => {
    if (loading && !config) {
      // Skeleton, not a spinner — the sidebar chrome is already up and
      // the form shape marks where the section content lands.
      return <ConfigurationSkeleton />;
    }

    if (error) {
      return (
        <div className={styles.errorContainer}>
          <h2>⚠️ Error Loading Configuration</h2>
          <p>{error}</p>
          <Button onClick={fetchActiveConfiguration}>Retry</Button>
        </div>
      );
    }

    if (!config) {
      return <div className={styles.noData}>No configuration available</div>;
    }

    switch (activeSection) {
      case SECTIONS.OVERVIEW:
        return <OverviewSection config={config} />;
      case SECTIONS.RISK_MATRIX:
        return <RiskMatrixEditor />;
      case SECTIONS.CAL_MATRIX:
        return <CALMatrixEditor />;
      case SECTIONS.CAL_CRITERIA:
        return <CALCriteriaEditor />;
      case SECTIONS.THRESHOLDS:
        return <ThresholdEditor />;
      case SECTIONS.SCORING_FACTORS:
        return <ScoringFactorsEditor />;
      case SECTIONS.FORMULAS:
        return <FormulaEditor />;
      case SECTIONS.IMPACT_RATINGS:
        return <ImpactRatingEditor />;
      case SECTIONS.VERSIONS:
        return <VersionHistory />;
      case SECTIONS.AUDIT_LOG:
        return <AuditLog />;
      default:
        return <div>Select a section</div>;
    }
  };

  const content = (
    <>
        {/* Two-pane SettingsModal-style layout — sidebar + content area
            inside a single bordered card. Title + version live in the
            sidebar header; Save / Discard actions sit in the sidebar
            footer when the config is modified. */}
        <div className={styles.mainContentInner}>
          {/* Left Navigation */}
          <aside className={styles.sidebar}>
            <div className={styles.sidebarHeader}>
              <h2 className={styles.sidebarTitle}>Project Configuration</h2>
              {config && (
                <Badge preset="default">Version {config.version}</Badge>
              )}
            </div>

            <nav>
              <div className={styles.sidebarSection}>
                <h3>Overview</h3>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.OVERVIEW ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.OVERVIEW)}
                >
                  <Icon name="overview" size={16} className={styles.navIcon} />
                  <span>Overview</span>
                </button>
              </div>

              <div className={styles.sidebarSection}>
                <h3>Matrices</h3>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.RISK_MATRIX ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.RISK_MATRIX)}
                >
                  <Icon name="grid" size={16} className={styles.navIcon} />
                  <span>Risk Matrix</span>
                </button>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.CAL_MATRIX ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.CAL_MATRIX)}
                >
                  <Icon name="shield" size={16} className={styles.navIcon} />
                  <span>CAL Matrix</span>
                </button>
              </div>

              <div className={styles.sidebarSection}>
                <h3>Scoring</h3>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.CAL_CRITERIA ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.CAL_CRITERIA)}
                >
                  <Icon name="list-checks" size={16} className={styles.navIcon} />
                  <span>CAL Criteria</span>
                </button>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.THRESHOLDS ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.THRESHOLDS)}
                >
                  <Icon name="sliders" size={16} className={styles.navIcon} />
                  <span>Thresholds</span>
                </button>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.SCORING_FACTORS ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.SCORING_FACTORS)}
                >
                  <Icon name="calculator" size={16} className={styles.navIcon} />
                  <span>Scoring Factors</span>
                </button>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.FORMULAS ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.FORMULAS)}
                >
                  <Icon name="formula" size={16} className={styles.navIcon} />
                  <span>Formulas</span>
                </button>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.IMPACT_RATINGS ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.IMPACT_RATINGS)}
                >
                  <Icon name="star" size={16} className={styles.navIcon} />
                  <span>Impact Ratings</span>
                </button>
              </div>

              <div className={styles.sidebarSection}>
                <h3>History</h3>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.VERSIONS ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.VERSIONS)}
                >
                  <Icon name="history" size={16} className={styles.navIcon} />
                  <span>Versions</span>
                </button>
                <button
                  className={`${styles.navItem} ${activeSection === SECTIONS.AUDIT_LOG ? styles.active : ''}`}
                  onClick={() => setActiveSection(SECTIONS.AUDIT_LOG)}
                >
                  <Icon name="document" size={16} className={styles.navIcon} />
                  <span>Audit Log</span>
                </button>
              </div>
            </nav>

            {isModified && (
              <div className={styles.sidebarFooter}>
                <Button
                  className={styles.footerBtn}
                  onClick={() => setShowSaveDialog(true)}
                  disabled={loading}
                  startIcon={<Icon name="save" size={14} />}
                >
                  Save Changes
                </Button>
                <Button
                  className={styles.footerBtn}
                  variant="destructive"
                  onClick={handleCancel}
                  disabled={loading}
                  startIcon={<Icon name="close" size={14} />}
                >
                  Discard
                </Button>
              </div>
            )}
          </aside>

          {/* Content Area */}
          <div className={styles.contentArea}>
            {renderSectionContent()}
          </div>
        </div>

        {/* Save Dialog — uses the shared FormModal primitive (same
            shell as Create New Project on the projects page). The
            primitive owns chrome, validation, focus trap, escape /
            backdrop close, and button styling; only the field schema
            + onSubmit live here. */}
        {showSaveDialog && (
          <FormModal
            title="Save Configuration"
            fields={SAVE_DIALOG_FIELDS}
            initialValues={saveMetadata}
            submitLabel="Save Configuration"
            submittingLabel="Saving..."
            onSubmit={handleSave}
            onClose={() => setShowSaveDialog(false)}
          />
        )}

        {confirmDialog}
    </>
  );

  if (embedded) return content;

  return (
    <PageLayout
      activeNavItem="admin-configuration"
      currentPage="Configuration Management"
      breadcrumbItems={[
        { label: 'Dashboard', path: '/athena' }
      ]}
      contentClassName={styles.pageContentArea}
      rightContent={<UserInfo />}
    >
      {content}
    </PageLayout>
  );
}

// Overview Section Component
function OverviewSection({ config }) {
  return (
    <div className={styles.overview}>
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>{config.version}</div>
          <div className={styles.statLabel}>Version</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {config.configuration.risk_matrix?.values ?
              Object.keys(config.configuration.risk_matrix.values).length : 0}
          </div>
          <div className={styles.statLabel}>Risk Matrix Entries</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {config.configuration.cal_matrix?.values ?
              Object.keys(config.configuration.cal_matrix.values).length : 0}
          </div>
          <div className={styles.statLabel}>CAL Matrix Entries</div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {config.configuration.attack_feasibility_thresholds?.levels ?
              Object.keys(config.configuration.attack_feasibility_thresholds.levels).length : 0}
          </div>
          <div className={styles.statLabel}>Threshold Levels</div>
        </div>
      </div>

      <div className={styles.infoSection}>
        <h3>Configuration Details</h3>
        <div className={styles.detailsGrid}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Description:</span>
            <span className={styles.detailValue}>{config.metadata.description}</span>
          </div>

          {config.metadata.notes && (
            <div className={styles.detailItem}>
              <span className={styles.detailLabel}>Notes:</span>
              <span className={styles.detailValue}>{config.metadata.notes}</span>
            </div>
          )}

          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Created By:</span>
            <span className={styles.detailValue}>{config.metadata.created_by}</span>
          </div>

          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Created At:</span>
            <span className={styles.detailValue}>
              {new Date(config.metadata.created_at).toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <InstructionPanel title="Quick Guide">
        <ul>
          <li><strong>Risk Matrix:</strong> Define risk values (1-5) based on impact and feasibility combinations</li>
          <li><strong>CAL Matrix:</strong> Set Cybersecurity Assurance Levels based on impact and attack vector</li>
          <li><strong>Thresholds:</strong> Configure attack feasibility score ranges</li>
          <li><strong>Scoring Factors:</strong> Adjust point values for attack potential factors</li>
          <li><strong>Versions:</strong> View history and rollback to previous configurations</li>
          <li><strong>Audit Log:</strong> Track all configuration changes</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}
